import type { ReactNode } from 'react';
import { objectivePace } from '@/lib/objective-progress';
import type {
  WorkspaceObjective,
  WorkspaceObjectiveMeasure,
  WorkspaceObjectiveProgress,
} from '@shared/types/workspace-objectives';

const shortDate = (isoDate: string) =>
  new Date(`${isoDate}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** What the count says about the week; nothing is estimated beyond arithmetic. */
const paceText = (
  objective: Pick<WorkspaceObjective, 'due_date'>,
  measure: WorkspaceObjectiveMeasure,
  total: number,
  thisWeek: number | null,
  today: Date,
): string => {
  const week = thisWeek === null ? null : `${thisWeek} cette semaine`;
  if (total >= measure.target_value) return ['Cible atteinte', week].filter(Boolean).join(' · ');
  const pace = objectivePace({ total, target: measure.target_value, dueDate: objective.due_date, today });
  if (pace.overdue && objective.due_date) return `Échéance passée le ${shortDate(objective.due_date)}`;
  const head = pace.perWeek !== null && objective.due_date
    ? `Reste ${pace.remaining} : ${pace.perWeek} par semaine d’ici le ${shortDate(objective.due_date)}`
    : `Reste ${pace.remaining}, compté depuis le ${shortDate(measure.starts_on)}`;
  return [head, week].filter(Boolean).join(' · ');
};

/** Count, bar and weekly pace of a measured objective. `heading` takes the
 * place of the list and status (small tiles of Aujourd'hui). */
export const ObjectiveProgressLine = ({ objective, measure, progress, today = new Date(), heading }: {
  objective: Pick<WorkspaceObjective, 'title' | 'due_date'>;
  measure: WorkspaceObjectiveMeasure;
  progress: WorkspaceObjectiveProgress | undefined;
  today?: Date;
  heading?: ReactNode;
}) => {
  const withHeading = (node: ReactNode) => heading ? <div className='space-y-1'>{heading}{node}</div> : node;
  if (!progress) return withHeading(<p role='status' className='text-xs text-domino-inkSoft'>Comptage…</p>);
  if (progress.reason === 'app_inaccessible') {
    return withHeading(<p className='text-xs leading-5 text-domino-inkSoft'>Mesuré sur une application à laquelle vous n’avez pas accès.</p>);
  }
  if (progress.reason === 'status_missing') {
    return withHeading(<p className='text-xs leading-5 text-domino-coralDark'>Mesure à revoir : ce statut n’existe plus dans la liste.</p>);
  }
  if (progress.reason === 'hubspot_pending') {
    return withHeading(<p role='status' className='text-xs text-domino-inkSoft'>Comptage…</p>);
  }
  if (progress.reason === 'hubspot_unavailable') {
    return withHeading(<p className='text-xs leading-5 text-domino-inkSoft'>HubSpot indisponible.</p>);
  }
  if (progress.reason === 'hubspot_owner_unmapped') {
    return withHeading(<p className='text-xs leading-5 text-domino-coralDark'>Responsable absent des propriétaires HubSpot.</p>);
  }
  const total = progress.total ?? 0;
  const percent = Math.min(100, Math.round((total / measure.target_value) * 100));
  return (
    <div>
      <div className='flex items-baseline justify-between gap-3'>
        {heading ?? (
          <span className='min-w-0 truncate text-xs text-domino-inkSoft' title={`${progress.statusLabel ?? ''} · ${progress.listTitle ?? ''}`}>
            {progress.statusLabel} · {progress.listTitle}
          </span>
        )}
        <span className='shrink-0 text-sm font-semibold tabular-nums text-domino-ink'>
          {total} / {measure.target_value}
        </span>
      </div>
      <div
        role='progressbar'
        aria-label={`Progression : ${objective.title}`}
        aria-valuemin={0}
        aria-valuemax={measure.target_value}
        aria-valuenow={total}
        className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-domino-muted'
      >
        <div className='h-full rounded-full bg-domino-aubergine' style={{ width: `${percent}%` }} />
      </div>
      <p className={`mt-1.5 text-xs leading-5 text-domino-inkSoft ${heading ? 'truncate' : ''}`} title={heading ? paceText(objective, measure, total, progress.thisWeek, today) : undefined}>
        {paceText(objective, measure, total, progress.thisWeek, today)}
      </p>
    </div>
  );
};
